import { createSelector, createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import { type RootState } from '@/platform/store/types';
import type { Organization } from '@/shared/api/types';

export interface OrganizationState {
  data: {
    organizations: Organization[];
    isLoading: boolean;
    skip: number;
    limit: number;
    page: number;
    total: number;
  };
  isLoadingDetail: boolean;
  currentOrganization: Organization | null;
}

const initialState: OrganizationState = {
  data: {
    organizations: [],
    isLoading: false,
    skip: 0,
    limit: 10,
    page: 1,
    total: 0,
  },
  isLoadingDetail: false,
  currentOrganization: null,
};

export const organizationSlice = createSlice({
  name: 'organizations',
  // Mounted at `organization` in the root reducer. `reducerPath` pins that key
  // for combineSlices without renaming the action type prefix.
  reducerPath: 'organization',
  initialState,
  reducers: {
    setOrganizations: (state, action: PayloadAction<Organization[]>) => {
      state.data.organizations = action.payload;
    },
    setLoadingOrganizations: (state, action: PayloadAction<boolean>) => {
      state.data.isLoading = action.payload;
    },
    setLoadingOrganization: (state, action: PayloadAction<boolean>) => {
      state.isLoadingDetail = action.payload;
    },
    setPagination: (
      state,
      action: PayloadAction<{
        skip: number;
        limit: number;
        page: number;
        total: number;
      }>,
    ) => {
      state.data.skip = action.payload.skip;
      state.data.limit = action.payload.limit;
      state.data.page = action.payload.page;
      state.data.total = action.payload.total;
    },
    setCurrentOrganization: (
      state,
      action: PayloadAction<Organization | null>,
    ) => {
      state.currentOrganization = action.payload;
    },
    addOrganization: (state, action: PayloadAction<Organization>) => {
      state.data.organizations.push(action.payload);
    },
    deleteOrganization: (state, action: PayloadAction<string>) => {
      state.data.organizations = state.data.organizations.filter(
        (c) => c._id !== action.payload,
      );
      if (state.currentOrganization?._id === action.payload) {
        state.currentOrganization = null;
      }
    },
    resetState: () => {
      return initialState;
    },
  },
});

export const {
  setOrganizations,
  setLoadingOrganizations,
  setLoadingOrganization,
  setPagination,
  setCurrentOrganization,
  addOrganization,
  deleteOrganization,
  resetState,
} = organizationSlice.actions;

// Selectors
export const selectOrganization = (state: RootState) =>
  state.organization.currentOrganization;
export const selectOrganizations = (state: RootState) =>
  state.organization.data.organizations;
export const selectIsLoadingOrganizations = (state: RootState) =>
  state.organization.data.isLoading;
export const selectIsLoadingOrganization = (state: RootState) =>
  state.organization.isLoadingDetail;

const selectPaginationState = (state: RootState) => state.organization.data;

export const selectPagination = createSelector(
  [selectPaginationState],
  (paginationState) => ({
    skip: paginationState.skip,
    limit: paginationState.limit,
    page: paginationState.page,
  }),
);

export const selectCurrentOrganization = (state: RootState) =>
  state.organization.currentOrganization;


